import {FormEvent, ReactNode, useEffect, useState} from "react";
import DataAPI from "../apis/DataAPI";
import {savAuth} from "../apis/axiosConfig";

const INPUT_CLASS =
  "block w-full rounded-lg border-2 border-[#B3B4D5] text-bpurple placeholder:text-gray-400 hover:border-[#8e8fb4] focus:outline-none py-3 pl-2 shadow-sm focus:border-[#0000ff] sm:text-sm sm:leading-6";
const STATUS_BAR_CLASS =
  "flex justify-end items-center gap-3 bg-[#e7e8f0] px-4 py-1 text-sm text-bpurple";

// Affiche les pages console seulement avec une session SAV (jeton de 12 h, cf. axiosConfig).
function SavLogin({children}: {children: ReactNode}) {
  const [loggedIn, setLoggedIn] = useState<boolean>(savAuth.isActive());
  const [login, setLogin] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  // Un 401 sur une route /sav/ (jeton expiré, mot de passe changé) ou « Log out » efface la
  // session : retour au formulaire. Connexion et déconnexion suivent les autres onglets.
  useEffect(() => {
    const onLost = () => {
      setLoggedIn(false);
      setPassword("");
    };
    const onStorage = () => setLoggedIn(savAuth.isActive());
    window.addEventListener("sav-auth-lost", onLost);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("sav-auth-lost", onLost);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      savAuth.set(await DataAPI.createSavSession(login, password));
      setPassword("");
      setLoggedIn(true);
    } catch (err: any) {
      const status = err?.response?.status;
      setError(
        status === 401
          ? "Wrong login or password"
          : status === 429
          ? "Too many failed attempts, try again in 15 minutes"
          : "Server unreachable, please try again"
      );
    }
    setLoading(false);
  };

  if (loggedIn)
    return (
      <>
        <div className={STATUS_BAR_CLASS}>
          <span>
            <span className="text-[green]">●</span> SAV connected until{" "}
            {new Date(savAuth.get()?.expires_at ?? 0).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
          <button
            type="button"
            onClick={() => savAuth.clear()}
            className="underline hover:text-bblue">
            Log out
          </button>
        </div>
        {children}
      </>
    );

  return (
    <div>
      <div className={STATUS_BAR_CLASS}>
        <span>
          <span className="text-gray-400">○</span> Not connected
        </span>
      </div>
      <div className="bg-bblue p-4 text-white">
        <h1 className="text-4xl text-center font-walsheim-bold">Console</h1>
      </div>
      <form onSubmit={submit} className="container mx-auto p-3 max-w-sm mt-10">
        <h2 className="text-bblue mb-4 text-xl font-walsheim-regular">
          SAV login
        </h2>
        <input
          name="username"
          autoComplete="username"
          placeholder="Login"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          className={INPUT_CLASS}
        />
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={`${INPUT_CLASS} mt-4`}
        />
        {error && <p className="italic text-[red] mt-2">{error}</p>}
        <button
          type="submit"
          disabled={loading || login === "" || password === ""}
          className="w-full mt-4 rounded-lg bg-bblue hover:bg-[#3333ff] text-white p-3 text-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
          {loading ? "..." : "Sign in"}
        </button>
      </form>
    </div>
  );
}

export default SavLogin;
